import type { ComponentPropsWithoutRef, ElementRef } from "react";
import { forwardRef } from "react";
import { ScrollArea as ScrollAreaPrimitive } from "radix-ui";
import { tv } from "tailwind-variants";

const styles = tv({
  slots: {
    scrollBar:
      "flex touch-none rounded-full transition-colors select-none data-[state=hidden]:animate-out data-[state=hidden]:fade-out-20 data-[state=hidden]:zoom-out-95 data-[state=visible]:animate-in data-[state=visible]:fade-in-0 data-[state=visible]:zoom-in-95",
    thumb: "relative flex-1 rounded-full bg-neutral-500 active:bg-neutral-600",
    root: "relative grow overflow-hidden",
    viewport: "h-full w-full rounded-[inherit] [&>div]:h-full",
  },
  variants: {
    orientation: {
      vertical: {
        scrollBar: "h-[calc(100%-0.5rem)] w-3 self-center p-[3px]",
      },
      horizontal: {
        scrollBar: "h-3 flex-col p-[3px]",
      },
    },
  },
});

const { scrollBar, thumb, root, viewport } = styles();

const ScrollBar = forwardRef<
  ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={scrollBar({ className, orientation })}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className={thumb()} />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

/**
 * @deprecated use the new `ScrollArea` component
 */
export const LegacyScrollArea = forwardRef<
  ElementRef<typeof ScrollAreaPrimitive.Root>,
  ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    viewportClassName?: string;
    orientation?: "vertical" | "horizontal";
  }
>(({ className, viewportClassName, orientation, children, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={root({ className })}
    {...props}
  >
    <ScrollAreaPrimitive.Viewport
      className={viewport({ className: viewportClassName })}
    >
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar className="pointer-events-auto z-50" orientation={orientation} />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
));
LegacyScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
